/**
 * CustomerSelect — durchsuchbare Kundenauswahl mit Inline-Neuanlage für das
 * Recurring-Template-Formular (M3b, Kundenauswahl-Fix).
 *
 * Ersetzt das native `<select>` (nur scrollbar, kein Teilstring-Filter, keine
 * Neu-Anlage) durch ein `CreatableSelect` (react-select) mit:
 *   - Tastatur-Teilstring-Filter über die geladene `customers`-Liste,
 *   - „+ Neuen Kunden anlegen": legt einen minimalen `customers`-Doc via
 *     `addDoc` an (Owner-scoped) und wählt ihn sofort aus.
 *
 * Ein Recurring-Template referenziert zwingend eine persistente `customerId`
 * (der PHP-Lauf lädt den Kunden zur Erzeugungszeit live nach) — deshalb ein
 * echter `customers`-Doc statt eines Freitext-Snapshots. Der angelegte Kunde ist
 * bewusst minimal (nur Name); Details ergänzt der Nutzer im Kunden-Bereich.
 *
 * @module components/recurring/CustomerSelect
 */

import React, { useMemo, useState, useCallback } from 'react';
import CreatableSelect from 'react-select/creatable';
import type { SingleValue, GroupBase } from 'react-select';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { reactSelectTokenStyles } from '../../styles/reactSelectTokenStyles';

/** Minimaler Kunden-Shape, wie ihn die Recurring-Seite lädt. */
export interface CustomerSelectOption {
  docId: string;
  name: string;
  companyName?: string | null;
  email?: string | null;
}

interface SelectOption {
  value: string;
  label: string;
}

interface CustomerSelectProps {
  /** Geladene Kundenliste (Optionsquelle). */
  customers: CustomerSelectOption[];
  /** Aktuell ausgewählte `customerId` ('' = keine Auswahl). */
  value: string;
  /** Callback bei Auswahländerung (neue `customerId` oder ''). */
  onChange: (customerId: string) => void;
  /**
   * Callback nach erfolgreicher Inline-Neuanlage — der Aufrufer soll die neue
   * Option in seine Kundenliste aufnehmen (damit das Label sofort korrekt ist).
   */
  onCustomerCreated: (customer: CustomerSelectOption) => void;
  /** Owner-/Tenant-Scope-UID für das `userId`-Feld des neuen Kunden-Docs. */
  ownerUid: string;
  /** Placeholder-Text (i18n). */
  placeholder?: string;
  /** Format-Funktion für „+ Neuen Kunden anlegen: X" (i18n). */
  formatCreateLabel?: (input: string) => string;
  /** Fehlermeldung bei fehlgeschlagener Anlage (i18n). */
  createErrorLabel?: string;
  /** aria-Label / Test-Hook. */
  inputId?: string;
}

/**
 * Durchsuchbare Kundenauswahl mit Inline-Neuanlage.
 */
const CustomerSelect: React.FC<CustomerSelectProps> = ({
  customers,
  value,
  onChange,
  onCustomerCreated,
  ownerUid,
  placeholder,
  formatCreateLabel,
  createErrorLabel,
  inputId,
}) => {
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const options = useMemo<SelectOption[]>(
    () =>
      customers.map((c) => ({
        value: c.docId,
        label: c.companyName || c.name || c.docId,
      })),
    [customers]
  );

  const selectedOption = useMemo<SelectOption | null>(
    () => options.find((o) => o.value === value) ?? null,
    [options, value]
  );

  const handleChange = useCallback(
    (option: SingleValue<SelectOption>) => {
      setCreateError(null);
      onChange(option ? option.value : '');
    },
    [onChange]
  );

  const handleCreate = useCallback(
    async (inputValue: string) => {
      const name = inputValue.trim();
      if (!name) return;

      setIsCreating(true);
      setCreateError(null);
      try {
        // Minimaler Owner-scoped Kunden-Doc — Firestore-Rules erlauben Owner-Create.
        const docRef = await addDoc(collection(db, 'customers'), {
          name,
          companyName: name,
          userId: ownerUid,
          createdAt: new Date(),
        });
        const created: CustomerSelectOption = {
          docId: docRef.id,
          name,
          companyName: name,
          email: null,
        };
        onCustomerCreated(created);
        onChange(docRef.id);
      } catch (err: unknown) {
        setCreateError(
          createErrorLabel ??
            (err instanceof Error ? err.message : String(err))
        );
      } finally {
        setIsCreating(false);
      }
    },
    [ownerUid, onChange, onCustomerCreated, createErrorLabel]
  );

  return (
    <div>
      <CreatableSelect<SelectOption, false, GroupBase<SelectOption>>
        inputId={inputId}
        classNamePrefix="react-select"
        value={selectedOption}
        onChange={handleChange}
        onCreateOption={handleCreate}
        options={options}
        isDisabled={isCreating}
        isLoading={isCreating}
        isClearable
        isSearchable
        placeholder={placeholder}
        styles={reactSelectTokenStyles}
        formatCreateLabel={formatCreateLabel}
      />
      {createError && (
        <p className="mt-1 text-xs text-[var(--tone-danger-fg)]" role="alert">
          {createError}
        </p>
      )}
    </div>
  );
};

export default CustomerSelect;
